import { call, cancel, cancelled } from "redux-saga/effects";
import { ResponseErrorTypes } from "~/EasyProjectsCommon/EpEnums";
import { UnsavedChangesNotifier } from "~/EasyProjectsCommon/UnsavedChangesNotifier";
import { HandlingConfiguration } from "~/Redux/Actions/errorActions";
import { withPromiseNotifier } from "~/sharedComponents/feedback";
import { PromiseNotifierArgs } from "~/sharedComponents/feedback/SavingNotifier";
import { ApiErrorTypes } from "../../Actions";
import { handleApiError } from "./handleApiError";

export const defaultAjaxOptions: Partial<OptionalParams> = {
    ignoreErrorsList: [
        ApiErrorTypes.ObjectNotFound,
        ApiErrorTypes.UserFriendlyError,
        ApiErrorTypes.ForbiddenError,
        ApiErrorTypes.InvalidParameters,
        ApiErrorTypes.UnknownError,
        ResponseErrorTypes.UpgradePlanRequired,
    ],
};

export interface CallApiParams {
    ajaxOptions: Partial<OptionalParams>;
}

type EffectArgs = {
    abortPrevious?: boolean;
    enableUnsavedChangesNotifier?: boolean;
    getErrorOptions?: HandlingConfiguration;
    retryCount?: number;
    suppressErrorsHandle?: boolean;
    promiseNotifierArgs?: PromiseNotifierArgs;
};

export const callApi = <P>(
    fn: F1<CallApiParams, PromiseLike<P>>,
    {
        enableUnsavedChangesNotifier,
        getErrorOptions,
        abortPrevious,
        retryCount = 1,
        suppressErrorsHandle = false,
        promiseNotifierArgs,
    }: EffectArgs = {}
) =>
    call(function* () {
        const options: CallApiParams = { ajaxOptions: defaultAjaxOptions };

        let xhr: PromiseLike<P>;
        let result: P;

        let showActionFeedback: (args?: any) => void, showErrorFeedback: (args?: any) => void;
        let promiseNotifier: ReturnType<typeof withPromiseNotifier>;

        if (promiseNotifierArgs) {
            promiseNotifier = withPromiseNotifier(
                new Promise((resolve, reject) => {
                    showActionFeedback = resolve;
                    showErrorFeedback = reject;
                }),
                promiseNotifierArgs
            );
        }

        while (retryCount > 0) {
            try {
                xhr = fn(options);

                if (enableUnsavedChangesNotifier) {
                    UnsavedChangesNotifier.instance.trackPromise(xhr as JQuery.Promise<any>);
                }

                result = yield xhr.then(x => x);
                showActionFeedback?.();

                return result;
            } catch (e) {
                retryCount--;
                if (retryCount <= 0) {
                    showErrorFeedback?.();

                    if (suppressErrorsHandle) {
                        throw e;
                    }

                    yield call(handleApiError, e, getErrorOptions);
                    yield cancel();
                }
            } finally {
                if (yield cancelled()) {
                    promiseNotifier?.hideToast();
                }

                if (abortPrevious && !result) {
                    (xhr as any)?.abort();
                }
            }
        }
    });
